import {Breadcrumb} from 'Core/models/Breadcrumb';
import * as A from 'fp-ts/lib/Array';
import {contramap, getTupleOrd, ordNumber, ordString} from 'fp-ts/lib/Ord';
import {pipe} from 'fp-ts/lib/pipeable';
import {nanoid} from 'nanoid';
import {createAction} from 'Shared/utilities/actionCreator';

enum BreadcrumbsActionType {
  ADD_BREADCRUMB = 'breadcrumbs/ADD_BREADCRUMB',
  REMOVE_BREADCRUMB = 'breadcrumbs/CLEAR_BREADCRUMB',
}

export const addBreadcrumb = (breadcrumb: Breadcrumb) =>
  createAction(BreadcrumbsActionType.ADD_BREADCRUMB, {breadcrumb, id: nanoid()});

export const removeBreadcrumb = (breadcrumbId: string) =>
  createAction(BreadcrumbsActionType.REMOVE_BREADCRUMB, {breadcrumbId});

type BreadcrumbsReducerState = {
  breadcrumbs: {[id: string]: Breadcrumb};
};

const DEFAULT_STATE: BreadcrumbsReducerState = {
  breadcrumbs: {},
};

type BreadcrumbsActions = ReturnType<typeof addBreadcrumb> | ReturnType<typeof removeBreadcrumb>;

export default (
  state: BreadcrumbsReducerState = DEFAULT_STATE,
  action: BreadcrumbsActions
): BreadcrumbsReducerState => {
  switch (action.type) {
    case BreadcrumbsActionType.ADD_BREADCRUMB:
      const {id, breadcrumb} = action.payload;
      const comparingBreadcrumbPathLength = contramap((breadcrumb: Breadcrumb) => breadcrumb.path.length)(ordNumber);

      const newCrumbsList = {
        ...state.breadcrumbs,
        [id]: breadcrumb,
      };

      const sortedCrumbsList = pipe(
        newCrumbsList,
        Object.keys,
        A.map((crumbKey): [Breadcrumb, string] => [newCrumbsList[crumbKey], crumbKey]),
        A.sort(getTupleOrd(comparingBreadcrumbPathLength, ordString)),
        A.reduce({}, (acc, [breadcrumb, crumbKey]) => ({...acc, [crumbKey]: breadcrumb}))
      );

      return {
        ...state,
        breadcrumbs: sortedCrumbsList,
      };
    case BreadcrumbsActionType.REMOVE_BREADCRUMB:
      const {breadcrumbId} = action.payload;
      const {breadcrumbs: currentBreadcrumbs} = state;
      delete currentBreadcrumbs[breadcrumbId];

      return {
        ...state,
        breadcrumbs: {...currentBreadcrumbs},
      };
    default:
      return state;
  }
};
